<template>
  <div class="bandejas">
    <!-- Una sola tabla parametrizada por bandeja. El backend hace el filtrado
         (GET /planes?bandeja=…), no el navegador: las bandejas deben coincidir
         con lo que cuenta el dashboard. -->
    <div class="chips">
      <button
        v-for="b in bandejas"
        :key="b.id"
        class="chip"
        :class="{ on: actual === b.id }"
        @click="seleccionar(b.id)"
      >
        {{ b.label }}
        <span v-if="conteos[b.id] != null" class="chip-n">{{ conteos[b.id] }}</span>
      </button>
    </div>

    <p class="desc">{{ descripcionActual }}</p>

    <div v-if="loading" class="estado">Cargando…</div>
    <div v-else-if="error" class="estado error">{{ error }}</div>
    <PlanTable v-else :planes="planes" :vacio="vacioActual" @abrir="abrir" />

    <PlanDetalle
      v-if="detalle"
      :plan="detalle"
      :sesion="sesion"
      @close="detalle = null"
      @actualizado="refrescarDetalle"
    />

    <AlertDialog
      v-if="alerta"
      variant="error"
      :title="alerta.title"
      :message="alerta.message"
      @close="alerta = null"
    />
  </div>
</template>

<script>
import PlanTable from "./PlanTable.vue";
import PlanDetalle from "./PlanDetalle.vue";
import AlertDialog from "./AlertDialog.vue";
import cxService from "../../../services/cx.service";

const BANDEJAS = [
  {
    id: "mis",
    label: "Mis pendientes",
    desc: "Planes abiertos donde eres el responsable.",
    vacio: "No tienes planes pendientes. ",
  },
  {
    id: "validacion",
    label: "Por validar",
    desc: "Planes que el responsable ya envió y esperan revisión de CX.",
    vacio: "Nada esperando validación.",
  },
  {
    id: "vencidos",
    label: "Vencidos",
    desc: "Pasaron su fecha compromiso vigente y siguen abiertos.",
    vacio: "Ningún plan vencido.",
  },
  {
    id: "proximos",
    label: "Próximos a vencer",
    desc: "Vencen en los próximos 5 días.",
    vacio: "Nada por vencer en los próximos días.",
  },
  {
    id: "cerrados",
    label: "Cerrados",
    desc: "Planes concluidos o cancelados.",
    vacio: "Todavía no hay planes cerrados.",
  },
];

export default {
  name: "BandejasTab",
  components: { PlanTable, PlanDetalle, AlertDialog },
  data() {
    return {
      bandejas: BANDEJAS,
      actual: "mis",
      planes: [],
      conteos: {},
      loading: true,
      error: "",
      detalle: null,
      alerta: null,
      sesion: {},
    };
  },
  computed: {
    def() {
      return BANDEJAS.find((b) => b.id === this.actual) || BANDEJAS[0];
    },
    descripcionActual() {
      return this.def.desc;
    },
    vacioActual() {
      return this.def.vacio;
    },
  },
  async mounted() {
    this.sesion = await cxService.sesion();
    // Para CX la bandeja útil de entrada es la de validación, no "mis planes".
    if (this.sesion.rol === "cx" || this.sesion.rol === "admin") {
      this.actual = "validacion";
    }
    await this.cargar();
    this.cargarConteos();
  },
  methods: {
    async seleccionar(id) {
      this.actual = id;
      await this.cargar();
    },
    async cargar() {
      this.loading = true;
      this.error = "";
      try {
        this.planes = await this.consultar(this.actual);
        this.conteos[this.actual] = this.planes.length;
      } catch (e) {
        this.error = "No se pudo cargar la bandeja. Revisa la conexión con el servicio.";
      } finally {
        this.loading = false;
      }
    },
    consultar(id) {
      if (id === "mis") {
        return cxService.fetchPlanes({
          bandeja: "mis",
          responsable: this.sesion.idColaborador,
        });
      }
      return cxService.fetchPlanes({ bandeja: id });
    },
    // Los contadores de las otras bandejas se piden en segundo plano: si alguna
    // falla, la pestaña activa ya se mostró y no se rompe nada.
    async cargarConteos() {
      const otras = BANDEJAS.filter((b) => b.id !== this.actual);
      const res = await Promise.allSettled(otras.map((b) => this.consultar(b.id)));
      res.forEach((r, i) => {
        if (r.status === "fulfilled") this.conteos[otras[i].id] = r.value.length;
      });
    },
    async abrir(p) {
      try {
        this.detalle = await cxService.fetchPlan(p.idPlan);
      } catch (e) {
        this.alerta = { title: "No se pudo abrir el plan", message: e.message };
      }
    },
    async refrescarDetalle() {
      if (!this.detalle) return;
      try {
        this.detalle = await cxService.fetchPlan(this.detalle.idPlan);
        await this.cargar();
        this.cargarConteos();
      } catch (e) {
        this.alerta = { title: "No se pudo actualizar el plan", message: e.message };
      }
    },
  },
};
</script>

<style scoped>
.bandejas {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.chips {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
.chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  background: var(--pt-card);
  color: var(--pt-dim);
  border: 1px solid var(--pt-border);
  cursor: pointer;
  font-family: inherit;
}
.chip.on {
  background: #003ca5;
  border-color: #003ca5;
  color: #fff;
}
.chip-n {
  font-size: 11px;
  font-weight: 700;
  background: rgba(148, 163, 184, 0.25);
  border-radius: 999px;
  padding: 1px 7px;
}
.chip.on .chip-n {
  background: rgba(255, 255, 255, 0.25);
}
.desc {
  margin: 0;
  font-size: 12px;
  color: var(--pt-muted);
}
.estado {
  text-align: center;
  font-size: 14px;
  color: var(--pt-muted);
  padding: 40px 0;
}
.estado.error {
  color: #b91c1c;
}
</style>
